import { styled } from '@linaria/react';
import { useTranslation } from 'react-i18next';
import type { SessionPullRequest } from '../../../../api/agent-miner-client/agent-miner-client.type';
import { primaryBase, redBase } from '../../../../styles/design-tokens';
import { fmtDurationLong, fmtNum, fmtRelative, parseBackendIso } from '../../shared/formatters';
import { StatTile, StatTileGrid } from '../../shared/stat-tile';

type PrOverviewTilesProps = {
  pr: SessionPullRequest;
};

const clampPct = (value: number): number => Math.max(0, Math.min(100, value));

/**
 * The four pull-request headline metrics rendered as page-layout overview tiles:
 * whole-PR agent authorship, agent-code survival, time-to-merge, and lines
 * changed. Shown in the session Overview panel while the PR tab is active, in
 * place of the session's own stat tiles.
 *
 * @param {PrOverviewTilesProps} props - The selected pull request
 * @returns {JSX.Element} The PR overview tile grid
 */
export function PrOverviewTiles({ pr }: PrOverviewTilesProps) {
  const { t } = useTranslation();

  const agentPct = pr.whole_pr.ai_percentage;
  const hasAuthorship = agentPct !== null && agentPct !== undefined;

  // Time to merge = merge timestamp − open timestamp (both naive-UTC, so the
  // difference is correct regardless of the viewer's zone). Null until merged.
  const timeToMergeMs =
    pr.merged_at && pr.created_at
      ? parseBackendIso(pr.merged_at).getTime() - parseBackendIso(pr.created_at).getTime()
      : null;

  return (
    <StatTileGrid>
      <StatTile
        label={t('agent_miner.pull_requests.overview.agent_authorship')}
        value={hasAuthorship ? `${Math.round(clampPct(agentPct))}%` : '—'}
        subtitle={t('agent_miner.pull_requests.overview.agent_authorship_sub')}
      />
      <StatTile
        label={t('agent_miner.pull_requests.overview.survived')}
        value={pr.survival.line_precise ? `${Math.round(clampPct(pr.survival.survival_rate))}%` : '—'}
        subtitle={t('agent_miner.pull_requests.overview.survived_sub')}
      />
      <StatTile
        label={t('agent_miner.pull_requests.overview.time_to_merge')}
        value={timeToMergeMs !== null ? fmtDurationLong(timeToMergeMs) : '—'}
        subtitle={
          pr.merged_at
            ? t('agent_miner.pull_requests.overview.merged_ago', { ago: fmtRelative(pr.merged_at) })
            : t('agent_miner.pull_requests.overview.not_merged')
        }
      />
      <StatTile
        label={t('agent_miner.pull_requests.overview.lines_changed')}
        value={
          <>
            <Additions>+{fmtNum(pr.additions)}</Additions> <Deletions>−{fmtNum(pr.deletions)}</Deletions>
          </>
        }
        subtitle={t('agent_miner.pull_requests.overview.files', { count: pr.changed_files })}
      />
    </StatTileGrid>
  );
}

const Additions = styled.span`
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${primaryBase};
`;

const Deletions = styled.span`
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${redBase};
`;
